'use client';

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  layoutShareBreadcrumbs,
  type BreadcrumbDisplaySegment,
  type BreadcrumbSegment,
} from '@/lib/shareBreadcrumbLayout';
import { usePublicUi } from '@/lib/publicUi';
import styles from './ShareViewer.module.css';

type Props = {
  segments: BreadcrumbSegment[];
  canGoBack: boolean;
  onBack: () => void;
  onNavigate: (uuid: string) => void;
  infoSlot: ReactNode;
  /** When false, the path-bar (i) control is omitted. */
  showInfo?: boolean;
  desktopExtras?: ReactNode;
  ownerSlot?: ReactNode;
};

export function ShareBreadcrumbBar({
  segments,
  canGoBack,
  onBack,
  onNavigate,
  infoSlot,
  showInfo = true,
  desktopExtras,
  ownerSlot,
}: Props) {
  const { extra } = usePublicUi();
  const pathSlotRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState<BreadcrumbDisplaySegment[]>(() =>
    segments.map((s, i) => ({
      uuid: s.uuid,
      label: s.title,
      ellipsis: false,
      isCurrent: i === segments.length - 1,
    })),
  );
  const [showTitleAbove, setShowTitleAbove] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const infoWrapRef = useRef<HTMLDivElement>(null);

  const fullCurrentTitle = segments[segments.length - 1]?.title ?? '';

  useLayoutEffect(() => {
    const slot = pathSlotRef.current;
    const probe = measureRef.current;
    if (!slot || !probe) return;

    const measure = (label: string) => {
      probe.textContent = label;
      return probe.getBoundingClientRect().width;
    };
    const sepWidth = (() => {
      probe.textContent = '/';
      const w = probe.getBoundingClientRect().width;
      return w + 10; // pathSep horizontal margins
    })();

    const apply = () => {
      const width = slot.clientWidth;
      const layout = layoutShareBreadcrumbs(segments, width, measure, sepWidth);
      setDisplay(layout.segments);
      setShowTitleAbove(layout.showTitleAbove);
    };

    apply();
    const ro = new ResizeObserver(() => apply());
    ro.observe(slot);
    return () => ro.disconnect();
  }, [segments]);

  useEffect(() => {
    if (!showInfo) setInfoOpen(false);
  }, [showInfo]);

  useEffect(() => {
    if (!infoOpen) return;
    const onPointer = (ev: PointerEvent) => {
      const el = infoWrapRef.current;
      if (!el) return;
      if (ev.target instanceof Node && el.contains(ev.target)) return;
      setInfoOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen]);

  return (
    <div className={styles.crumbBar}>
      {showTitleAbove && fullCurrentTitle ? (
        <div className={styles.pathTitleAbove} title={fullCurrentTitle}>
          {fullCurrentTitle}
        </div>
      ) : null}
      <div className={styles.crumbRow}>
        <button
          type="button"
          className={styles.backBtn}
          disabled={!canGoBack}
          onClick={onBack}
          aria-label={extra.shareGoBack}
        >
          <span className={styles.backIcon} aria-hidden>
            ←
          </span>
          <span className={styles.backText}>{extra.shareBack.replace(/^←\s*/, '')}</span>
        </button>
        <div className={styles.pathSlot} ref={pathSlotRef}>
          <nav className={styles.pathNav} aria-label={extra.shareCanvasPath}>
            {display.map((seg, i) => (
              <span key={`${seg.uuid}-${i}`} className={styles.pathSeg}>
                {i > 0 ? (
                  <span className={styles.pathSep} aria-hidden>
                    /
                  </span>
                ) : null}
                {seg.isCurrent ? (
                  <span className={styles.pathCurrent} title={fullCurrentTitle}>
                    {seg.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.pathLink}
                    onClick={() => onNavigate(seg.uuid)}
                    title={
                      seg.ellipsis
                        ? segments.find((s) => s.uuid === seg.uuid)?.title
                        : seg.label
                    }
                  >
                    {seg.label}
                  </button>
                )}
              </span>
            ))}
          </nav>
        </div>
        {desktopExtras}
        {showInfo ? (
          <div className={styles.infoWrap} ref={infoWrapRef}>
            <button
              type="button"
              className={styles.infoBtn}
              aria-expanded={infoOpen}
              aria-label={extra.sharePathInfo}
              onClick={() => setInfoOpen((o) => !o)}
            >
              i
            </button>
            {infoOpen ? <div className={styles.infoPanel}>{infoSlot}</div> : null}
          </div>
        ) : null}
        {ownerSlot ? <div className={styles.ownerBlock}>{ownerSlot}</div> : null}
      </div>
      <span ref={measureRef} className={styles.pathMeasure} aria-hidden />
    </div>
  );
}
